import { sectionContainerAtom } from '@ingenico/react-section-utils';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useMediaQuery } from '@mui/material';
import deepEqual from 'fast-deep-equal';
import { createStore, Provider, useAtom } from 'jotai';
import { toString } from 'lodash-es';
import type { ReactElement } from 'react';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { deviceManagementContainer } from '../../container.const';
import { openLogRequestModalAtom } from '../create-log-request-modal/CreateLogRequestModal.atom';
import { openViewLogModalAtom } from '../device-logs-modal/DeviceLogModal.atom';
import type { LogicalDeviceLogsProps, LogicalDeviceLogsState } from '../../domain/models/logicalDeviceLogs.model';
import { useCapabilityCreateLogcat } from '../../hooks/useCapabilityCreateLogcat.hook';
import { useDeviceLogsModal } from '../../hooks/useDeviceLogsModal.hook';
import { useDeviceManagementCapability } from '../../hooks/useDeviceManagementCapability.hook';
import { useGetDeviceIdV2 } from '../../hooks/useGetDeviceIdV2.hook';
import { useLogRequests } from '../../hooks/useLogRequest.hook';
import { useShouldShowViewLogs } from '../../hooks/useShouldShowViewLogs.hook';

function ExposedLogicalDeviceLogsContent({
  isAttachedDevice,
  isDeviceArchived,
  logicalDeviceId,
  onStateChange,
}: LogicalDeviceLogsProps): ReactElement {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.LOGICAL_DEVICE]);
  const canReadLogs = useDeviceManagementCapability('logicalDevice.logcat.read');
  const {
    canDisplayKebabMenuDeviceLogs,
    canDisplaySubItemMenuCreateLogs,
    isEnabledSubItemMenuCreateLogs,
    errorMessage,
    refetchIsInProgress,
  } = useCapabilityCreateLogcat({ isAttachedDevice, deviceId: logicalDeviceId });
  const device = useGetDeviceIdV2({ deviceId: logicalDeviceId, shouldFetch: isAttachedDevice && canReadLogs });
  const { deviceLogsComponent, handleShowDeviceLogsModal } = useDeviceLogsModal({
    logicalDeviceId,
    connectionStatus: device.data?.connectionStatus,
    refetchIsInProgress,
  });
  const { createLogRequestComponent, handleCreateLogRequestDialog } = useLogRequests({
    logicalDeviceId,
    onSubmission: () => {
      handleShowDeviceLogsModal();
      refetchIsInProgress();
    },
  });
  const shouldShowViewLogs = useShouldShowViewLogs({ logicalDeviceId });
  const [isLogRequestModalOpen, setIsLogRequestModalOpen] = useAtom(openLogRequestModalAtom);
  const [isViewLogModalOpen, setIsViewLogModalOpen] = useAtom(openViewLogModalAtom);
  const isMobile = useMediaQuery('(max-width: 766px)');

  const handleCloseMobileDialog = useCallback(() => {
    setIsLogRequestModalOpen(false);
    setIsViewLogModalOpen(false);
  }, [setIsLogRequestModalOpen, setIsViewLogModalOpen]);

  const menuOption = useMemo<LogicalDeviceLogsState['menuOption']>(() => {
    if (!canDisplayKebabMenuDeviceLogs || isDeviceArchived) return undefined;

    const childrenLabel = [];
    if (canDisplaySubItemMenuCreateLogs) {
      childrenLabel.push({
        disabled: !isEnabledSubItemMenuCreateLogs,
        icon: <AddCircleOutlineOutlinedIcon />,
        label: toString(t('button.logRequest')),
        onClick: handleCreateLogRequestDialog,
        tooltip: errorMessage,
        dataTestId: 'li-log-request',
      });
    }
    childrenLabel.push({
      disabled: !shouldShowViewLogs,
      icon: <DescriptionOutlinedIcon />,
      label: t('button.viewLogs'),
      onClick: handleShowDeviceLogsModal,
      dataTestId: 'li-view-log',
      tooltip: shouldShowViewLogs ? undefined : t('deviceLogsModal.error.noLogs'),
    });

    return {
      disabled: false,
      icon: <DescriptionOutlinedIcon />,
      label: t('button.deviceLogs'),
      dataTestId: 'li-device-logs',
      childrenLabel,
    };
  }, [
    canDisplayKebabMenuDeviceLogs,
    canDisplaySubItemMenuCreateLogs,
    errorMessage,
    handleCreateLogRequestDialog,
    handleShowDeviceLogsModal,
    isDeviceArchived,
    isEnabledSubItemMenuCreateLogs,
    shouldShowViewLogs,
    t,
  ]);

  const state = useMemo<LogicalDeviceLogsState>(
    () => ({
      menuOption,
      isMobileDialogOpen: isMobile && (isLogRequestModalOpen || isViewLogModalOpen),
      handleCloseMobileDialog,
    }),
    [handleCloseMobileDialog, isLogRequestModalOpen, isMobile, isViewLogModalOpen, menuOption],
  );

  useEffect(() => onStateChange(state), [onStateChange, state]);

  return isMobile ? (
    <></>
  ) : (
    <>
      {createLogRequestComponent}
      {deviceLogsComponent}
    </>
  );
}

function ExposedLogicalDeviceLogsCpnt(props: LogicalDeviceLogsProps): ReactElement {
  const store = useMemo(() => {
    const sectionStore = createStore();
    sectionStore.set(sectionContainerAtom, deviceManagementContainer);
    return sectionStore;
  }, []);

  return (
    <Provider store={store}>
      <ExposedLogicalDeviceLogsContent {...props} />
    </Provider>
  );
}

export const ExposedLogicalDeviceLogs = memo(ExposedLogicalDeviceLogsCpnt, deepEqual);
